import {
  API_ENDPOINT,
  fetchWithAuth,
  getAuthToken,
  parseApiResponse,
} from "./index";

export type MembershipTier =
  | "ieee_student_life_retired"
  | "ieee_member"
  | "non_ieee_member";

export type PrimaryCategory = "full_conference" | "single_day" | "test";

export type RegistrationPolicy = "dtpi" | "paper_plus_addon";

export interface FormatRateTicket {
  id: number;
  name: string;
  typeName: string | null;
  membershipTier: MembershipTier | null;
  participationFormat: "in_person" | "virtual" | null;
  pricePence: number;
  currency: string;
  itemOrderedLabel: string | null;
}

export interface RegistrationCatalog {
  testPricing?: {
    enabled: boolean;
    totalUsdCents: number | null;
    label: string | null;
  };
  event: { id: number; slug: string; title: string };
  registrationPolicy?: RegistrationPolicy;
  formatPriced?: boolean;
  formatRates?: {
    fullConference: FormatRateTicket[];
    addOn: FormatRateTicket[];
    singleDay: FormatRateTicket[];
  } | null;
  activePricingWindow: "earlyBird" | "standard" | "onSite" | null;
  activePricingWindowLabel: string | null;
  pricingWindows: {
    earlyBird: { endsAt: string };
    standard: { endsAt: string };
    onSite: { endsAt: string | null };
  } | null;
  pricingWindowOptions: {
    key: string;
    label: string;
    dateRange: string;
    isActive: boolean;
  }[] | null;
  fullConference: {
    id: number;
    name: string;
    membershipTier: MembershipTier;
    itemOrderedLabel: string | null;
    currency: string;
    ratesUsd: Record<string, number> | null;
    currentPricePence: number;
  }[];
  singleDay: {
    id: number;
    name: string;
    membershipTier: MembershipTier;
    itemOrderedLabel: string | null;
    pricePence: number;
    currency: string;
  }[];
  banquet: {
    id: number;
    name: string;
    itemOrderedLabel: string | null;
    pricePence: number;
    currency: string;
  } | null;
  extraPageFee: {
    id: number;
    includedPages: number;
    pricePerPagePence: number;
    currency: string;
  } | null;
  testTicket: {
    id: number;
    name: string;
    itemOrderedLabel: string | null;
    pricePence: number;
    currency: string;
  } | null;
  additionalPaperFee?: {
    id: number | null;
    pricePence: number;
    currency: string;
    description: string;
  } | null;
  countries: { name: string; code: string }[];
  usStates: { name: string; code: string }[];
}

export interface AddOnRegistrantInput {
  name: string;
  email: string;
  membershipTier?: MembershipTier;
  participationFormat?: "in_person" | "virtual";
  memberId?: string;
}

export interface RegistrationPaperInput {
  paperId: string;
  paperTitle: string;
  paperCount: number;
  /** paper_plus_addon (idass/icast) only — DTPI ignores this field. */
  addOnRegistrants?: AddOnRegistrantInput[];
}

export interface SavedRegistrationPaperInput {
  paperId: string;
  paperTitle: string;
  paperCount?: number;
  addOnRegistrants?: AddOnRegistrantInput[];
}

export interface RegistrationDraftInput {
  firstName: string;
  lastName: string;
  companyName?: string;
  registrantState?: string;
  registrantCountry: string;
  membershipTier: MembershipTier;
  memberId?: string;
  papers?: RegistrationPaperInput[];
  participationFormat: "in_person" | "virtual" | "on_demand";
  primaryCategory: PrimaryCategory;
  banquetQuantity: number;
  discountCode?: string;
  successUrl: string;
  cancelUrl: string;
}

export interface RegistrationDraftResult {
  registrationId: string;
  checkoutUrl: string;
  sessionId: string;
  orderId: number;
  totalAmountPence: number;
  currency: string;
  activePricingWindow: string | null;
}

export interface RegistrationRecord {
  registrationId: string;
  email: string;
  firstName: string;
  lastName: string;
  companyName: string | null;
  registrantState: string | null;
  registrantCountry: string;
  registrationType: string;
  memberId: string | null;
  author: string;
  participationFormat: string;
  primaryCategory: string;
  itemOrdered: string;
  banquetQuantity: number;
  amountOrderedPence: number;
  totalAfterDiscountPence: number;
  amountPaidPence: number;
  currency: string;
  status: string;
  registrationDate: string | null;
  event: { title: string; slug: string };
  order: {
    status: string;
    orderNumber: string | null;
    stripeCheckoutSessionId: string | null;
  } | null;
}

/** Short-lived catalog cache so event + rates can share one in-flight request. */
const CATALOG_CACHE_TTL_MS = 30_000;
const catalogCache = new Map<
  string,
  { token: string | null; fetchedAt: number; promise: Promise<RegistrationCatalog> }
>();

export function invalidateRegistrationCatalogCache(slug?: string): void {
  if (slug) catalogCache.delete(slug);
  else catalogCache.clear();
}

export async function getRegistrationCatalog(
  slug: string,
): Promise<RegistrationCatalog> {
  const token = getAuthToken();
  const cached = catalogCache.get(slug);
  if (
    cached &&
    cached.token === token &&
    Date.now() - cached.fetchedAt < CATALOG_CACHE_TTL_MS
  ) {
    return cached.promise;
  }

  const promise = (async () => {
    const response = await fetchWithAuth(
      `${API_ENDPOINT}/events/${slug}/registration-catalog`,
    );
    return parseApiResponse<RegistrationCatalog>(response);
  })();

  catalogCache.set(slug, { token, fetchedAt: Date.now(), promise });
  try {
    return await promise;
  } catch (err) {
    catalogCache.delete(slug);
    throw err;
  }
}

export async function createRegistrationDraft(
  slug: string,
  input: RegistrationDraftInput,
): Promise<RegistrationDraftResult> {
  const response = await fetchWithAuth(
    `${API_ENDPOINT}/events/${slug}/registrations/draft`,
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
  return parseApiResponse<RegistrationDraftResult>(response);
}

export async function getRegistration(
  registrationId: string,
): Promise<RegistrationRecord> {
  const response = await fetch(
    `${API_ENDPOINT}/registrations/${registrationId}`,
  );
  const data = await parseApiResponse<{ registration: RegistrationRecord }>(
    response,
  );
  return data.registration;
}

export interface MyRegistration {
  registrationId: string;
  eventId: number;
  email: string;
  firstName: string;
  lastName: string;
  registrationType: string;
  itemOrdered: string;
  banquetQuantity: number;
  participationFormat: string;
  primaryCategory: string;
  amountPaidPence: number;
  currency: string;
  status: string;
  registrationDate: string | null;
  event: { title: string; slug: string };
  order: {
    id: number;
    orderNumber: string | null;
    status: string;
    stripeCheckoutSessionId: string | null;
  } | null;
}

export async function getMyRegistrations(): Promise<MyRegistration[]> {
  const response = await fetchWithAuth(`${API_ENDPOINT}/registrations/me`);
  const data = await parseApiResponse<{ registrations: MyRegistration[] }>(
    response,
  );
  return data.registrations;
}

export interface RegistrationAddOnCheckoutInput {
  banquetQuantity: number;
  successUrl: string;
  cancelUrl: string;
}

export interface RegistrationAddOnCheckoutResult {
  checkoutUrl: string;
}

export async function createRegistrationAddOnCheckout(
  registrationId: string,
  input: RegistrationAddOnCheckoutInput,
): Promise<RegistrationAddOnCheckoutResult> {
  const response = await fetchWithAuth(
    `${API_ENDPOINT}/registrations/${registrationId}/add-ons/checkout`,
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
  return parseApiResponse<RegistrationAddOnCheckoutResult>(response);
}

export interface SavedRegistrationFormData {
  firstName?: string;
  lastName?: string;
  companyName?: string;
  country?: string;
  countrySearch?: string;
  countrySelectedFromList?: boolean;
  state?: string;
  membershipTier?: MembershipTier;
  memberId?: string;
  papers?: SavedRegistrationPaperInput[];
  moreThanOnePaper?: boolean;
  additionalPaperCount?: number;
  addOnRegistrants?: AddOnRegistrantInput[];
  participationFormat?: "in_person" | "virtual";
  primaryCategory?: PrimaryCategory;
  banquetQuantity?: number;
}

export interface SavedRegistration {
  id: number;
  step: number;
  formData: SavedRegistrationFormData;
  updatedAt: string;
  event: { title: string; slug: string; startDate: string | null };
}

export async function getMySavedRegistrations(): Promise<SavedRegistration[]> {
  const response = await fetchWithAuth(`${API_ENDPOINT}/registrations/saved`);
  const data = await parseApiResponse<{ saved: SavedRegistration[] }>(response);
  return data.saved;
}

export async function getSavedRegistration(
  slug: string,
): Promise<SavedRegistration | null> {
  const response = await fetchWithAuth(
    `${API_ENDPOINT}/events/${slug}/registrations/saved`,
  );
  const data = await parseApiResponse<{ saved: SavedRegistration | null }>(
    response,
  );
  return data.saved;
}

export async function saveRegistrationProgress(
  slug: string,
  step: number,
  formData: SavedRegistrationFormData,
): Promise<SavedRegistration> {
  const response = await fetchWithAuth(
    `${API_ENDPOINT}/events/${slug}/registrations/saved`,
    {
      method: "PUT",
      body: JSON.stringify({ step, formData }),
    },
  );
  const data = await parseApiResponse<{ saved: SavedRegistration }>(response);
  return data.saved;
}

export async function deleteSavedRegistration(slug: string): Promise<void> {
  const response = await fetchWithAuth(
    `${API_ENDPOINT}/events/${slug}/registrations/saved`,
    { method: "DELETE" },
  );
  await parseApiResponse(response);
}
